// =============================================================================
// Chat Sidebar – Restructured: 2 sections + quick filter bar
// Clean, modern, high-density with subtle color accents
// =============================================================================

import React, { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  ChevronRight,
  ChevronLeft,
  CircleDot,
  MessageCircle,
  Search,
  Plus,
  Megaphone,
  Star,
  Globe,
  LayoutGrid,
  Briefcase,
  Send,
  X,
  Users,
  Hash,
  Bell,
  Inbox,
} from "lucide-react";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useChatStore } from "../hooks/useChatStore";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TOOLTIP_STYLES } from "@/config/design-system";
import { NewChannelDialog, CHANNEL_ICON_MAP } from "./NewChannelDialog";
import { MemberStatusAvatar } from "@/components/ui/member-status-avatar";
import type { ChatChannel } from "../types";

type CreationType = 'dm' | 'group' | 'channel';
type QuickFilter = 'all' | 'unread' | 'favorites';

// Accent palette – muted, professional tones
const ACCENTS = {
  channels:  { dot: "#0EA5E9", text: "#0369A1" },
  dms:       { dot: "#8B5CF6", text: "#6D28D9" },
} as const;

// Scope sub-labels
const SCOPE_LABELS: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  workspace: { label: "Workspace", icon: Globe, color: "#EC4899" },
  space:     { label: "Espace",    icon: LayoutGrid, color: "#F97316" },
  project:   { label: "Projet",    icon: Briefcase, color: "#0EA5E9" },
};

// Distinct color palette for workspace channels
const CHANNEL_COLORS = [
  { dot: "#EC4899", text: "#BE185D" },
  { dot: "#8B5CF6", text: "#6D28D9" },
  { dot: "#0EA5E9", text: "#0369A1" },
  { dot: "#10B981", text: "#047857" },
  { dot: "#F97316", text: "#C2410C" },
  { dot: "#EF4444", text: "#B91C1C" },
  { dot: "#6366F1", text: "#4338CA" },
  { dot: "#14B8A6", text: "#0F766E" },
  { dot: "#F59E0B", text: "#B45309" },
  { dot: "#06B6D4", text: "#0E7490" },
  { dot: "#A855F7", text: "#7E22CE" },
  { dot: "#84CC16", text: "#4D7C0F" },
] as const;

interface ChatSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

function sortChannels(channels: ChatChannel[], unreadCounts: Record<string, number>): ChatChannel[] {
  return [...channels].sort((a, b) => {
    // 1. Pinned first
    const aPinned = (a as any)._pinned ? 1 : 0;
    const bPinned = (b as any)._pinned ? 1 : 0;
    if (aPinned !== bPinned) return bPinned - aPinned;
    // 2. Last activity (most recent first)
    const aTime = a.last_message?.created_at || a.updated_at || a.created_at;
    const bTime = b.last_message?.created_at || b.updated_at || b.created_at;
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  });
}

export function ChatSidebar({ collapsed, onToggle }: ChatSidebarProps) {
  const { currentOrganization, currentRole } = useOrganization();
  const isAdmin = currentRole === 'owner' || currentRole === 'admin';
  const isGuest = currentRole === 'guest';
  const {
    channels, activeChannelId, setActiveChannel,
    loadMessages, markRead, loadingChannels, unreadCounts,
  } = useChatStore();

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    channels: true, dms: true,
  });
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const [newChannelTab, setNewChannelTab] = useState<CreationType>('dm');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');

  const toggle = (key: string) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const select = (id: string) => {
    setActiveChannel(id);
    loadMessages(id);
    markRead(id);
  };

  // Group into 2 sections: channels (workspace+space+project) and conversations (DMs+groups)
  const grouped = useMemo(() => {
    let f = channels.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()));
    
    // Apply quick filter
    if (quickFilter === 'unread') {
      f = f.filter(c => (unreadCounts[c.id] || 0) > 0);
    } else if (quickFilter === 'favorites') {
      f = f.filter(c => (c as any)._pinned);
    }

    return {
      channels: sortChannels(f.filter(c => !c.is_dm), unreadCounts),
      dms: sortChannels(f.filter(c => c.is_dm), unreadCounts),
    };
  }, [channels, search, unreadCounts, quickFilter]);

  const totalUnread = useMemo(() => Object.values(unreadCounts).reduce((s: number, c: number) => s + c, 0), [unreadCounts]);

  const sectionUnread = useMemo(() => ({
    channels: grouped.channels.reduce((s, c) => s + (unreadCounts[c.id] || 0), 0),
    dms: grouped.dms.reduce((s, c) => s + (unreadCounts[c.id] || 0), 0),
  }), [grouped, unreadCounts]);

  const favCount = useMemo(() => channels.filter(c => (c as any)._pinned).length, [channels]);
  const unreadCount = useMemo(() => channels.filter(c => (unreadCounts[c.id] || 0) > 0).length, [channels, unreadCounts]);

  // ─── COLLAPSED ──────────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <aside className="flex flex-col w-12 shrink-0 border-r border-border h-full bg-card">
        <div className="h-12 flex items-center justify-center">
          <Button variant="ghost" size="icon" className="w-8 h-8" onClick={onToggle}>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center py-2 gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="relative w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors">
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
                {totalUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-0.5 rounded-full text-[9px] font-bold flex items-center justify-center text-white bg-destructive">
                    {totalUnread > 99 ? "99+" : totalUnread}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" style={TOOLTIP_STYLES}>Discussion</TooltipContent>
          </Tooltip>
        </div>
      </aside>
    );
  }

  // ─── SECTION HEADER ─────────────────────────────────────────────────────
  const Section = ({
    label, sectionKey, icon: Icon, unread, action,
  }: {
    label: string; sectionKey: string;
    icon: React.ElementType; unread?: number; action?: React.ReactNode;
  }) => {
    const accent = ACCENTS[sectionKey as keyof typeof ACCENTS];
    const isOpen = expanded[sectionKey];
    return (
      <div className="flex items-center group">
        <button
          className="flex items-center gap-1.5 px-3 py-[5px] flex-1 text-left rounded-md hover:bg-muted/60 transition-colors"
          onClick={() => toggle(sectionKey)}
        >
          <ChevronRight
            className={cn(
              "h-3 w-3 text-muted-foreground/60 transition-transform duration-150",
              isOpen && "rotate-90"
            )}
          />
          <span
            className="h-[6px] w-[6px] rounded-full shrink-0"
            style={{ backgroundColor: accent?.dot }}
          />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          {(unread ?? 0) > 0 && (
            <span
              className="ml-auto min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
              style={{ backgroundColor: accent?.dot }}
            >
              {unread}
            </span>
          )}
        </button>
        {action && (
          <div className="pr-1">
            {action}
          </div>
        )}
      </div>
    );
  };

  // ─── SCOPE BADGE ────────────────────────────────────────────────────────
  const ScopeBadge = ({ channel }: { channel: ChatChannel }) => {
    const scope = SCOPE_LABELS[channel.scope_type];
    if (!scope || channel.scope_type === 'workspace') return null;
    const scopeName = (channel as any).scope_name;
    if (!scopeName) return null;
    // For project channels, show "Discussion projet" instead of repeating the project name
    const displayLabel = channel.scope_type === 'project' ? 'Discussion projet' : scopeName;
    return (
      <span
        className="text-[9px] font-medium px-1 py-0 rounded truncate max-w-[100px]"
        style={{ color: scope.color, backgroundColor: `${scope.color}12` }}
      >
        {displayLabel}
      </span>
    );
  };

  // ─── CHANNEL ITEM ───────────────────────────────────────────────────────
  const Item = ({ channel }: { channel: ChatChannel }) => {
    const isActive = activeChannelId === channel.id;
    const unread = unreadCounts[channel.id] || 0;
    const dmProfile = channel._dm_profile;
    const isAnnonces = channel.write_policy === "admins_only";
    const isPinned = (channel as any)._pinned;

    // Workspace channels get distinct colors from the palette
    const accent = (!channel.is_dm && channel.scope_type === "workspace")
      ? CHANNEL_COLORS[Math.abs(Array.from(channel.id).reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0)) % CHANNEL_COLORS.length]
      : channel.is_dm ? ACCENTS.dms : ACCENTS.channels;

    // Detect group conversation: is_dm but no dm_profile (more than 2 members)
    const isGroupConversation = channel.is_dm && !dmProfile;

    // Icon rendering
    const icon = (() => {
      // Group conversation: show 2-letter initials of group name
      if (isGroupConversation) {
        const initials = channel.name.slice(0, 2).toUpperCase();
        const hue = Array.from(channel.id).reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0) % 360;
        return (
          <div
            className="h-[22px] w-[22px] rounded-full shrink-0 flex items-center justify-center text-[9px] font-semibold text-white"
            style={{ backgroundColor: `hsl(${Math.abs(hue)}, 55%, 50%)` }}
          >
            {initials}
          </div>
        );
      }
      // 1:1 DM — use MemberStatusAvatar
      if (channel.is_dm) {
        const initial = (dmProfile?.full_name || dmProfile?.email || "?").slice(0, 1).toUpperCase();
        return (
          <MemberStatusAvatar
            orgMemberId={dmProfile?.org_member_id}
            src={dmProfile?.avatar_url}
            fallback={initial}
            size="sm"
            className="h-[22px] w-[22px] shrink-0"
          />
        );
      }
      // Custom icon from DB (takes priority)
      if (channel.icon && CHANNEL_ICON_MAP[channel.icon]) {
        const IconComp = CHANNEL_ICON_MAP[channel.icon];
        return <IconComp className="h-3.5 w-3.5 shrink-0" style={{ color: isActive ? accent.dot : "hsl(var(--muted-foreground))" }} />;
      }
      
      if (isAnnonces) {
        return <Megaphone className="h-3.5 w-3.5 shrink-0 text-amber-500" />;
      }
      if (channel.name.toLowerCase().includes("général") || channel.name.toLowerCase() === "general") {
        return <CircleDot className="h-3.5 w-3.5 shrink-0" style={{ color: isActive ? accent.dot : "hsl(var(--muted-foreground))" }} />;
      }
      return <Hash className="h-3.5 w-3.5 shrink-0" style={{ color: isActive ? accent.dot : "hsl(var(--muted-foreground))" }} />;
    })();

    return (
      <button
        data-testid="chat-channel-item"
        data-channel-id={channel.id}
        data-channel-name={channel.name}
        className={cn(
          "flex items-center gap-2 w-full text-left rounded-md transition-all duration-100",
          "px-3 py-[5px]",
          isActive
            ? "bg-primary/8 text-foreground"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          unread > 0 && !isActive && "text-foreground font-medium",
        )}
        onClick={() => select(channel.id)}
      >
        {/* Active indicator bar */}
        <span
          className={cn(
            "w-[3px] self-stretch rounded-full shrink-0 transition-colors",
            isActive ? "bg-primary" : "bg-transparent"
          )}
        />
        {icon}
        <div className="flex flex-col min-w-0 flex-1">
          <span className={cn(
            "truncate text-[12.5px]",
            unread > 0 && !isActive && "bg-primary/10 text-foreground font-semibold px-1.5 py-0.5 rounded-md"
          )}>{channel.name}</span>
          {!channel.is_dm && channel.scope_type !== 'workspace' && (
            <ScopeBadge channel={channel} />
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isPinned && <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />}
          {unread > 0 && (
            <span
              className="min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
              style={{ backgroundColor: accent.dot }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </div>
      </button>
    );
  };

  // ─── QUICK FILTER BAR ──────────────────────────────────────────────────
  const filters: { key: QuickFilter; label: string; icon: React.ElementType; count?: number }[] = [
    { key: 'all', label: 'Tout', icon: Inbox },
    { key: 'unread', label: 'Non lus', icon: Bell, count: unreadCount },
    { key: 'favorites', label: 'Favoris', icon: Star, count: favCount },
  ];

  // ─── EXPANDED SIDEBAR ───────────────────────────────────────────────────
  return (
    <aside className="flex flex-col w-[260px] max-w-[260px] shrink-0 border-r border-border h-full bg-card">
      {/* Header */}
      <div className="h-12 flex items-center justify-between px-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <MessageCircle className="h-3.5 w-3.5 text-primary" />
          </div>
          <span className="font-semibold text-[13px] text-foreground">Discussion</span>
        </div>
        <Button variant="ghost" size="icon" className="w-7 h-7 text-muted-foreground hover:text-foreground" onClick={onToggle}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* Search */}
      <div className="px-3 pt-2 pb-1 shrink-0">
        <div className={cn(
          "relative flex items-center rounded-md border transition-colors",
          searchFocused ? "border-primary/40 bg-background" : "border-transparent bg-muted/50"
        )}>
          <Search className="absolute left-2 h-3.5 w-3.5 text-muted-foreground/60" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Rechercher..."
            className="w-full h-7 pl-7 pr-7 text-[12px] bg-transparent outline-none text-foreground placeholder:text-muted-foreground/50"
          />
          {search && (
            <button
              className="absolute right-1.5 h-4 w-4 rounded-sm flex items-center justify-center hover:bg-muted text-muted-foreground"
              onClick={() => setSearch("")}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Quick Filter Bar */}
      <div className="px-3 pb-2 pt-1 flex gap-1 shrink-0">
        {filters.map(f => {
          const isActive = quickFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setQuickFilter(f.key)}
              className={cn(
                "flex items-center gap-1 h-6 px-2 rounded-md text-[10.5px] font-medium transition-all",
                isActive
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground/70 hover:bg-muted/60 hover:text-muted-foreground"
              )}
            >
              <f.icon className={cn("h-3 w-3", isActive && f.key === 'favorites' && "fill-current")} />
              {f.label}
              {f.count != null && f.count > 0 && (
                <span className={cn(
                  "min-w-[14px] h-3.5 px-0.5 rounded-full text-[9px] font-bold flex items-center justify-center",
                  isActive ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground/60"
                )}>
                  {f.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Channel List */}
      {/*
        Override Radix ScrollArea viewport's internal `display: table` wrapper,
        which otherwise sizes to its widest child (long DM/group names) and
        pushes the section "+" buttons out of alignment. Forcing block + w-full
        keeps the inner content constrained to the sidebar width and lets
        `truncate` actually truncate long names.
      */}
      <ScrollArea className="flex-1 [&>div>div]:!block [&>div>div]:!w-full">
        {loadingChannels && channels.length === 0 ? (
          <div className="flex flex-col gap-2 px-3 py-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-[5px] animate-pulse">
                <div className="h-[22px] w-[22px] rounded-full bg-muted shrink-0" />
                <div className="flex-1 h-3 rounded bg-muted" style={{ width: `${55 + (i * 7) % 30}%` }} />
              </div>
            ))}
          </div>
        ) : (
        <div className="py-1 space-y-0.5">
          {/* Channels (Workspace + Spaces + Projects merged) */}
          <div>
            <Section
              label="Canaux"
              sectionKey="channels" icon={Hash} unread={sectionUnread.channels}
              action={isAdmin ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      onClick={() => { setNewChannelTab('channel'); setShowNewDM(true); }}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" style={TOOLTIP_STYLES}>Nouveau canal</TooltipContent>
                </Tooltip>
              ) : undefined}
            />
            {expanded.channels && (
              <div className="mt-0.5">
                {grouped.channels.length > 0
                  ? grouped.channels.map(c => <Item key={c.id} channel={c} />)
                  : <p className="px-8 py-1.5 text-[11px] text-muted-foreground/40 italic">
                      {quickFilter !== 'all' ? 'Aucun canal correspondant' : 'Aucun canal'}
                    </p>
                }
              </div>
            )}
          </div>

          {/* Conversations (DMs + Groups) */}
          <div>
            <Section
              label="Conversations" sectionKey="dms" icon={Send} unread={sectionUnread.dms}
              action={!isGuest ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      onClick={() => { setNewChannelTab('dm'); setShowNewDM(true); }}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" style={TOOLTIP_STYLES}>Nouvelle conversation</TooltipContent>
                </Tooltip>
              ) : undefined}
            />
            {expanded.dms && (
              <div className="mt-0.5">
                {grouped.dms.length > 0
                  ? grouped.dms.map(c => <Item key={c.id} channel={c} />)
                  : (
                    <div className="px-8 py-2">
                      <p className="text-[11px] text-muted-foreground/40 italic">
                        {quickFilter !== 'all' ? 'Aucune conversation correspondante' : 'Aucune conversation'}
                      </p>
                    </div>
                  )
                }
              </div>
            )}
          </div>

          {/* Global empty */}
          {channels.length === 0 && !loadingChannels && (
            <div className="px-4 py-8 text-center">
              <MessageCircle className="h-7 w-7 mx-auto mb-2 text-muted-foreground/20" />
              <p className="text-[11px] text-muted-foreground/50">
                Aucun canal disponible.<br />Créez un projet ou un espace.
              </p>
            </div>
          )}
        </div>
        )}
      </ScrollArea>

      <NewChannelDialog open={showNewDM} onOpenChange={setShowNewDM} defaultTab={newChannelTab} />
    </aside>
  );
}
